import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'

import { useTexte, WORTLAUT } from '../texte'
import { useEscape } from '../hooks/useEscape'
import { LoadingBar } from './LoadingBar'

/**
 * Die eigene Rückfrage — statt `window.confirm`.
 *
 * Der Browserdialog kann den zerstörenden Knopf weder rot machen noch beim
 * Namen nennen: er heißt immer „OK", egal ob dahinter ein Löschen steht.
 * Hier heißt der Knopf, was er tut („Löschen", „Wegnehmen"), trägt die
 * Warnfarbe, und **Abbrechen ist die sichere Vorgabe** — Escape und ein
 * Tipp auf den Hintergrund sagen Nein.
 *
 * Benutzt wie `confirm`: `if (await rueckfrage('Diese Nachricht löschen?',
 * 'Löschen')) …`. Der `RueckfrageHost` steht einmal in der `Shell`; sollte
 * er je fehlen, fällt die Funktion auf `window.confirm` zurück — eine
 * hässliche Rückfrage ist besser als ein stilles Ja.
 */
/**
 * More than a question (1.113.0, the sheet before a link opens): lines under
 * it, warnings in their colour, a button that is not dangerous, and a call
 * **inside the tap**.
 *
 * `beimJa` runs synchronously in the button's click, before the promise
 * settles. A `window.open()` after `await rueckfrage(…)` would run a
 * microtask later -- Chromium counts that as the tap, Safari does not
 * promise to (the same rule as for passkeys in CLAUDE.md).
 */
export interface Zusatz {
  details?: string[]
  /** The warnings, each one line; `data-warnung` carries its key. */
  warnungen?: { schluessel: string; text: string }[]
  /** `'normal'`: the button does not carry the colour of danger. */
  art?: 'gefahr' | 'normal'
  beimJa?: () => void
  /** A mark for the prüfstand on the sheet. */
  merkmal?: string
}

interface Frage extends Zusatz {
  frage: string
  knopf: string
  antwort: (ja: boolean) => void
  /** While something loads (`ladeblatt()`): the bar instead of the yes. */
  laden?: Ladestand
}

/** What the sheet shows while loading (1.160.0). */
interface Ladestand {
  /** Which sheet this is: a late report of a closed one changes nothing. */
  nummer: number
  geladen: number
  gesamt: number | null
  zeile: (geladen: number, gesamt: number | null) => string
  abbrechen: () => void
}

let stellen: ((frage: Frage) => void) | null = null
let aendern: ((aenderung: (alt: Frage | null) => Frage | null) => void) | null = null
let ladeNummer = 0
/**
 * The loading sheet that stands now, read synchronously: an updater runs only
 * at the next render, and whether `bereit()` still has a sheet to turn into
 * a question must be known in the call (CLAUDE.md, 1.24.0).
 */
let offeneLadung: number | null = null

export function rueckfrage(frage: string, knopf: string = WORTLAUT.allgemein.ja, zusatz: Zusatz = {}): Promise<boolean> {
  offeneLadung = null
  if (stellen === null) {
    const ja = window.confirm([frage, ...(zusatz.details ?? []), ...(zusatz.warnungen ?? []).map((w) => w.text)].join('\n'))
    if (ja) zusatz.beimJa?.()
    return Promise.resolve(ja)
  }
  const fragen = stellen
  return new Promise((antwort) => fragen({ frage, knopf, antwort, ...zusatz }))
}

/**
 * A sheet while files load, with a bar and "Abbrechen" (1.160.0), that turns
 * into the question when they are there.
 *
 * From use: "es steht unten in der Fußzeile, dass der Download erfolgt.
 * speziell bei langsamer Verbindung dauert es aber, bis der Speichern Dialog
 * erscheint. besser wäre hier auch ein Fenster mit Fortschrittsbalken (mit
 * Möglichkeit abzubrechen)". The same host as the question and not a second
 * one: on iPhone and iPad the loading sheet *becomes* the sheet with
 * "Sichern" (`bereit()`), and two sheets one after the other would blink.
 */
export interface Ladeblatt {
  /** Bytes so far, and of all when known. */
  stand: (geladen: number, gesamt: number | null) => void
  /** The files are there: the sheet becomes the question, answered like `rueckfrage()`. */
  bereit: (frage: string, knopf: string, zusatz?: Zusatz) => Promise<boolean>
  /** Close without a question: the download follows at once, or it failed. */
  zu: () => void
}

export function ladeblatt(
  frage: string,
  zeile: (geladen: number, gesamt: number | null) => string,
  abbrechen: () => void,
  merkmal?: string,
): Ladeblatt {
  const nummer = ++ladeNummer
  const dieses = (alt: Frage | null) => alt !== null && alt.laden?.nummer === nummer
  if (aendern !== null) {
    offeneLadung = nummer
    aendern(() => ({
      frage,
      knopf: '',
      merkmal,
      antwort: () => {},
      laden: { nummer, geladen: 0, gesamt: null, zeile, abbrechen },
    }))
  }
  return {
    stand: (geladen, gesamt) => {
      aendern?.((alt) => (dieses(alt) && alt?.laden ? { ...alt, laden: { ...alt.laden, geladen, gesamt } } : alt))
    },
    bereit: (frageJetzt, knopf, zusatz = {}) => {
      // Without a host, as `rueckfrage()`: the browser's own question.
      if (aendern === null) return rueckfrage(frageJetzt, knopf, zusatz)
      // Cancelled in between, or replaced: no question any more.
      if (offeneLadung !== nummer) return Promise.resolve(false)
      offeneLadung = null
      const setzen = aendern
      return new Promise((antwort) => setzen(() => ({ frage: frageJetzt, knopf, antwort, merkmal, ...zusatz })))
    },
    zu: () => {
      if (offeneLadung !== nummer) return
      offeneLadung = null
      aendern?.((alt) => (dieses(alt) ? null : alt))
    },
  }
}

export function RueckfrageHost() {
  const t = useTexte().allgemein
  const [offen, setOffen] = useState<Frage | null>(null)

  useEffect(() => {
    stellen = (frage) => setOffen(frage)
    aendern = (aenderung) => setOffen(aenderung)
    return () => {
      stellen = null
      aendern = null
    }
  }, [])

  // Enter bleibt bewusst unbelegt: die gefährliche Antwort soll ein
  // gezielter Tipp sein, kein Durchwinken.
  useEscape(offen === null ? null : () => beantworten(false))

  if (offen === null) return null

  function beantworten(ja: boolean) {
    // Cancelled while loading: the fetch stops, and the caller hears it as
    // an abort, not as a "no" to a question nobody asked yet.
    if (offen?.laden !== undefined) {
      offeneLadung = null
      offen.laden.abbrechen()
    } else if (ja) offen?.beimJa?.()
    offen?.antwort(ja)
    setOffen(null)
  }

  const laden = offen.laden

  return createPortal(
    <div
      className="rueckfrage"
      role="alertdialog"
      aria-modal="true"
      aria-label={offen.frage}
      onClick={() => beantworten(false)}
    >
      <div className="rueckfrage__blatt" onClick={(event) => event.stopPropagation()} data-rueckfrage={offen.merkmal}>
        <p className="rueckfrage__frage">{offen.frage}</p>
        {laden !== undefined && (
          <div className="rueckfrage__laden" data-laden="">
            {laden.gesamt !== null && laden.gesamt > 0
              ? <progress className="rueckfrage__balken" value={Math.min(laden.geladen, laden.gesamt)} max={laden.gesamt} aria-label={offen.frage} />
              : <LoadingBar label={offen.frage} />}
            <p className="rueckfrage__detail" data-laden-stand="">{laden.zeile(laden.geladen, laden.gesamt)}</p>
          </div>
        )}
        {offen.details?.map((zeile, i) => (
          <p key={`d${i}`} className="rueckfrage__detail">{zeile}</p>
        ))}
        {offen.warnungen !== undefined && offen.warnungen.length > 0 && (
          <ul className="rueckfrage__warnungen">
            {offen.warnungen.map((w) => (
              <li key={w.schluessel} data-warnung={w.schluessel}>{w.text}</li>
            ))}
          </ul>
        )}
        <div className="rueckfrage__knoepfe">
          <button
            type="button"
            className="button button--outline button--small"
            onClick={() => beantworten(false)}
            // Der sichere Weg bekommt den Fokus -- wer blind Enter drückt,
            // löscht nichts.
            autoFocus
          >
            {t.abbrechen}
          </button>
          {laden === undefined && (
            <button
              type="button"
              className={offen.art === 'normal' ? 'button button--small' : 'button button--small rueckfrage__gefahr'}
              onClick={() => beantworten(true)}
              data-rueckfrage-ja=""
            >
              {offen.knopf}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  )
}
